import { useCallback } from 'react';
import { useUIModeStore, type UIMode } from '../stores/uiModeStore';
import { UI_MODE_CONFIG, type SectionId } from '../config/uiModeConfig';

/**
 * Hook pour accéder et modifier le mode UI (Simple/Expert)
 *
 * Usage:
 * ```tsx
 * const { mode, isSimpleMode, changeMode, shouldShowSection, hasHydrated } = useUIMode();
 *
 * // Attendre l'hydratation avant d'afficher le menu
 * if (!hasHydrated) return <LoadingSpinner />;
 *
 * // Vérifier le mode
 * if (isSimpleMode) { ... }
 *
 * // Changer le mode
 * changeMode('expert');
 *
 * // Vérifier si une section doit être affichée
 * {shouldShowSection('treasury') && <TreasuryMenu />}
 * ```
 */
export function useUIMode() {
  const { mode, setMode, toggleMode, isSimpleMode, isExpertMode, hasHydrated } = useUIModeStore();

  /**
   * Change le mode UI and persists it in localStorage via Zustand.
   * Backend sync is not implemented: the UI mode preference is local-only,
   * stored in localStorage through the uiModeStore persist middleware.
   * If cross-device sync is needed in the future, POST to
   * UserPreference.dashboardLayout.uiMode on mode change.
   */
  const changeMode = useCallback((newMode: UIMode) => {
    setMode(newMode);
  }, [setMode]);

  /**
   * Vérifie si une section du sidebar doit être affichée
   * selon le mode actuel
   *
   * IMPORTANT: Pendant l'hydratation (hasHydrated=false), on affiche
   * tout en mode "expert" pour éviter le flash où des éléments
   * disparaissent puis réapparaissent.
   */
  const shouldShowSection = useCallback((sectionId: SectionId): boolean => {
    // Pendant l'hydratation, afficher tout pour éviter le flash
    if (!hasHydrated) return true;

    // En mode Expert, tout est visible
    if (mode === 'expert') return true;

    // En mode Simple, seules certaines sections sont visibles
    return UI_MODE_CONFIG.simple.visibleSections.includes(sectionId);
  }, [mode, hasHydrated]);

  /**
   * Vérifie si une route est accessible en mode Simple
   * (pour éventuellement rediriger)
   */
  const isRouteAllowed = useCallback((path: string): boolean => {
    if (mode === 'expert') return true;

    return UI_MODE_CONFIG.simple.visibleRoutes.some(route =>
      path.startsWith(route) || path === route
    );
  }, [mode]);

  /**
   * Obtient la configuration complète du mode actuel
   */
  const getModeConfig = useCallback(() => {
    return mode === 'simple' ? UI_MODE_CONFIG.simple : UI_MODE_CONFIG.expert;
  }, [mode]);

  return {
    // État
    mode,
    isSimpleMode: isSimpleMode(),
    isExpertMode: isExpertMode(),
    hasHydrated, // true quand le localStorage a été lu

    // Actions
    changeMode,
    toggleMode,

    // Helpers
    shouldShowSection,
    isRouteAllowed,
    getModeConfig,
  };
}
